import { Component, Input, Output, EventEmitter, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { interval, Subscription, takeUntil, fromEvent, tap, catchError, of } from 'rxjs';
import { ScheduleControlErrorsService } from 'src/app/settings/services/schedule-control.errors.service';

@Component({
  selector: 'app-input-date-customized',
  templateUrl: './input-date-customized.component.html',
  styleUrls: ['./input-date-customized.component.scss'],
})
export class InputDateCustomizedComponent implements OnDestroy {
  @Output() valueChange = new EventEmitter<string>();

  @Input() label: string = 'Label';
  @Input() value: string = '';
  @Input() step: number = 1; // Step in minutes
  @Input() tooltip: string = '';
  @Input() disabled: boolean = false;
  @Input() unit: string = '';
  @Input() restrictByStep: boolean = false;
  @Input() largeQuantity: boolean = false;
  @Input() placeholder: string = '--:--';
  /** Control for large quantities in the step, p.e if step is a larger decimal */
  @Input() pressIntervalMs: number = 50;
  @Input() fieldName: string = '';
  @Input() scheduleId: number = 0;

  hour: number = 0;
  minute: number = 0;

  @ViewChild('inputDate') inputDateRef: ElementRef<HTMLInputElement>;

  @ViewChild('upButton') upButton!: ElementRef<HTMLButtonElement>;
  @ViewChild('downButton') downButton!: ElementRef<HTMLButtonElement>;

  private incrementSubscription: Subscription | null = null;
  private decrementSubscription: Subscription | null = null;

  private disabledPressIntervalMs: number = 5000;

  errors: string[] = [];

  constructor(private readonly scheduleControlErrorsService: ScheduleControlErrorsService) {
    this.scheduleControlErrorsService.errors$.subscribe((errors) => {
      if (errors) {
        if (errors?.scheduleId === this.scheduleId) {
          this.errors = errors.fields[this.fieldName];
        } else {
          this.errors = [];
        }
      }
    });
  }

  // @HostListener('mouseleave', ['$event'])
  // onMouseLeave(event: MouseEvent): void {
  //   this.stopAnyActiveIntervals(true);
  // }

  // @HostListener('mouseenter', ['$event'])
  // onMouseEnter(event: MouseEvent): void {
  //   this.stopAnyActiveIntervals(true);
  // }

  onValueChange(newValue: string): void {
    if (this.disabled) {
      return;
    }

    // Parse the time value
    if (newValue && newValue.includes(':')) {
      const parts = newValue.split(':');
      this.hour = parseInt(parts[0]) || 0;
      this.minute = parseInt(parts[1]) || 0;
    } else {
      this.hour = 0;
      this.minute = 0;
    }

    this.value = newValue;
    this.emitValueChange();
    this.stopAnyActiveIntervals();
  }

  private getPressIntervalMs() {
    // 400ms for enabled and 5000ms for disabled
    // the 400ms cannot be too low, because it could bug the input increment/decrement handling
    // the 5000ms is to avoid opening doors to bug behavior if user click and hold the button in the disabled state
    return !this.disabled ? this.pressIntervalMs : this.disabledPressIntervalMs;
  }

  // This creates a "press and hold" functionality
  startIncrementing(): void {
    this.stopAnyActiveIntervals();

    this.incrementSubscription = interval(this.getPressIntervalMs())
      .pipe(
        takeUntil(fromEvent(this.upButton.nativeElement, 'mouseup')),
        takeUntil(fromEvent(this.upButton.nativeElement, 'mouseleave')),
        tap(() => this.incrementValue()),
        catchError((error) => {
          this.stopAnyActiveIntervals();
          return of(null);
        })
      )
      .subscribe();
  }

  stopIncrementing(withEmit: boolean = false): void {
    if (this.incrementSubscription) {
      this.incrementSubscription.unsubscribe();
      this.incrementSubscription = null;
    }
    if (withEmit) this.emitValueChange();
  }

  incrementValue(): void {
    if (this.disabled) {
      this.stopAnyActiveIntervals();
      return;
    }

    // Add step minutes
    this.minute += this.step;

    // Handle overflow
    if (this.minute >= 60) {
      const hoursToAdd = Math.floor(this.minute / 60);
      this.hour += hoursToAdd;
      this.minute = this.minute % 60;
    }

    // Handle 24-hour overflow
    if (this.hour >= 24) {
      this.hour = this.hour % 24;
    }

    // Update the input value
    this.updateInputValue();
    this.emitValueChange();
  }

  // This creates a "press and hold" functionality
  startDecrementing(): void {
    this.stopAnyActiveIntervals();

    this.decrementSubscription = interval(this.getPressIntervalMs())
      .pipe(
        takeUntil(fromEvent(this.downButton.nativeElement, 'mouseup')),
        takeUntil(fromEvent(this.downButton.nativeElement, 'mouseleave')),
        tap(() => this.decrementValue())
      )
      .subscribe();
  }

  stopDecrementing(withEmit: boolean = false): void {
    if (this.decrementSubscription) {
      this.decrementSubscription.unsubscribe();
      this.decrementSubscription = null;
    }
    if (withEmit) this.emitValueChange();
  }

  decrementValue(): void {
    if (this.disabled) {
      this.stopAnyActiveIntervals();
      return;
    }

    // Subtract step minutes
    this.minute -= this.step;

    // Handle underflow
    if (this.minute < 0) {
      const hoursToSubtract = Math.ceil(Math.abs(this.minute) / 60);
      this.hour -= hoursToSubtract;
      this.minute = 60 + (this.minute % 60);
      if (this.minute === 60) this.minute = 0;
    }

    // Handle negative hour underflow
    if (this.hour < 0) {
      this.hour = 24 + (this.hour % 24);
    }

    // Update the input value
    this.updateInputValue();
    this.emitValueChange();
  }

  private stopAnyActiveIntervals(withEmit: boolean = false): void {
    this.stopIncrementing(withEmit);
    this.stopDecrementing(withEmit);
  }

  private updateInputValue(): void {
    const formattedTime = this.formatTime(this.hour, this.minute);
    this.value = formattedTime;
  }

  private formatTime(hour: number, minute: number): string {
    const paddedHour = hour.toString().padStart(2, '0');
    const paddedMinute = minute.toString().padStart(2, '0');
    return `${paddedHour}:${paddedMinute}`;
  }

  private emitValueChange(): void {
    this.valueChange.emit(this.value);
  }

  ngOnDestroy(): void {
    this.stopAnyActiveIntervals();
  }
}
